import React, { useEffect, useState } from 'react';
import {
  Button,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from 'reactstrap';
import axios from 'axios';
import { toast } from 'react-toastify';

import { api } from '../../../config';
import logger from '../../../utils/logger';

const ACCEPTED_EXTENSIONS = ['.xlsx', '.xls'];

const hasAcceptedExtension = fileName =>
  ACCEPTED_EXTENSIONS.some(ext => fileName.toLowerCase().endsWith(ext));

const UploadParcelModal = ({
  isOpen,
  toggle,
  procurementId,
  token,
  onUploaded,
}) => {
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  // bersihkan pilihan sebelumnya setiap kali modal dibuka
  useEffect(() => {
    if (isOpen) {
      setFile(null);
      setFileError('');
    }
  }, [isOpen]);

  const handleFileChange = event => {
    const selected = event.target.files?.[0] ?? null;

    if (selected && !hasAcceptedExtension(selected.name)) {
      setFile(null);
      setFileError('File must be in .xlsx or .xls format.');
      return;
    }

    setFile(selected);
    setFileError('');
  };

  const handleClose = () => {
    if (isUploading) return;
    toggle();
  };

  const handleUpload = async () => {
    if (!file) {
      setFileError('Please choose a file first.');
      return;
    }

    if (!token) {
      toast.error('Authentication required. Please login again.');
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('File', file);
      if (procurementId) {
        formData.append('ProcurementId', procurementId);
      }

      await axios.post(
        `${api.API_URL}/quantity-incoterm-location-detail/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast.success('Parcel file uploaded successfully.');
      onUploaded?.();
      toggle();
    } catch (err) {
      // Interceptor axios global menolak dengan string (bahkan undefined),
      // bukan Error — jangan baca .message langsung.
      const status = err?.response?.status;
      logger.error(
        'Error uploading parcel file:',
        status ? `HTTP ${status}` : (err?.message ?? String(err))
      );
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          'Failed to upload parcel file.'
      );
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered>
      <ModalHeader toggle={handleClose} className='border-0 pb-0'>
        <span className='fw-semibold'>Upload Freight Request</span>
        <span className='d-block text-muted small fw-normal mt-1'>
          Upload the Parcel Request Freight Availability file that has already
          been filled in.
        </span>
      </ModalHeader>

      <ModalBody>
        <hr className='mt-0' />
        <Label for='parcel-upload-file' className='form-label fw-semibold'>
          File
        </Label>
        <Input
          id='parcel-upload-file'
          type='file'
          accept={ACCEPTED_EXTENSIONS.join(',')}
          onChange={handleFileChange}
          invalid={Boolean(fileError)}
          disabled={isUploading}
        />
        <FormFeedback>{fileError}</FormFeedback>
      </ModalBody>

      <ModalFooter className='border-0'>
        <Button
          color='link'
          className='text-decoration-none'
          onClick={handleClose}
          disabled={isUploading}
        >
          Close
        </Button>
        <Button
          color='primary'
          onClick={handleUpload}
          disabled={isUploading}
          className='d-flex align-items-center gap-1'
        >
          {isUploading && <Spinner size='sm' />} Upload File
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default UploadParcelModal;
